import React, { useState, useEffect, useMemo } from "react";
import { AgenticAPI } from "@/services/api";
import type { PodInstance, PodTemplate } from "@/services/api/agentic/types";
import { LoadingIndicator } from "../indicators/LoadingIndicator";
import {
  podCurrentStatusToDisplayString,
} from "@/utils/podStatus";

interface LogsOverviewProps {
  templateId?: string;
  podTemplate?: PodTemplate;
}

export function LogsOverview({ templateId, podTemplate }: LogsOverviewProps) {
  const [instances, setInstances] = useState<PodInstance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstances = async () => {
      setLoading(true);
      try {
        const response = await AgenticAPI.getPodInstances({
          template_id: templateId,
          limit: 500, // Get up to 500 instances for accurate counts
        });

        let fetchedInstances: PodInstance[] = [];
        if (response && (response as any).results) {
          fetchedInstances = (response as any).results;
        } else if (Array.isArray(response)) {
          fetchedInstances = response as any[];
        }

        setInstances(fetchedInstances);
      } catch (error) {
        console.error("Error fetching pod instances:", error);
        setInstances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInstances();
  }, [templateId]);

  // Categorize all instances by status using pod template status_values
  const categorizedInstances = useMemo(() => {
    // Get status values from pod template workflow_config
    const statusValues = podTemplate?.workflow_config?.status_values || [];

    if (statusValues.length === 0) {
      // Fallback if no status values defined
      return {
        first: [],
        inProgress: [],
        last: [],
        firstLabel: "Incoming",
        lastLabel: "Completed",
      };
    }

    const firstStatus = statusValues[0]?.label || "";
    const lastStatus = statusValues[statusValues.length - 1]?.label || "";
    const middleStatuses = statusValues
      .slice(1, -1)
      .map((sv: any) => sv.label || "");

    const first: PodInstance[] = [];
    const inProgress: PodInstance[] = [];
    const last: PodInstance[] = [];

    instances.forEach((instance) => {
      const instanceStatus =
        podCurrentStatusToDisplayString(instance.state?.current_status) || "";

      if (instanceStatus === firstStatus) {
        first.push(instance);
      } else if (instanceStatus === lastStatus) {
        last.push(instance);
      } else if (middleStatuses.includes(instanceStatus)) {
        inProgress.push(instance);
      } else {
        // Default to in progress if status doesn't match any defined values
        inProgress.push(instance);
      }
    });

    return {
      first: first.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
      inProgress: inProgress.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
      last: last.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
      firstLabel: firstStatus,
      lastLabel: lastStatus,
    };
  }, [instances, podTemplate]);

  return (
    <div className="flex flex-col space-y-4">
      {/* Summary cards in row layout */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200/80 bg-slate-100 p-4 flex h-32 items-center justify-center dark:border-transparent dark:bg-slate-700/50">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="text-4xl font-bold text-slate-900 dark:text-white min-h-12 flex items-center justify-center">
              {loading ? (
                <LoadingIndicator
                  dotCount={3}
                  dotSize={4}
                  dotSpacing={3}
                  animationType="wave"
                  showText={false}
                />
              ) : (
                categorizedInstances.first.length
              )}
            </div>
            <span className="text-xs uppercase tracking-widest font-mono  text-slate-600 dark:text-slate-400">
              {categorizedInstances.firstLabel}
            </span>
          </div>
        </div>

        <div className="flex h-32 items-center justify-center rounded-2xl border border-slate-200/80 bg-slate-100 p-4 dark:border-transparent dark:bg-slate-700/50">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="text-4xl font-bold text-slate-900 dark:text-white min-h-12 flex items-center justify-center">
              {loading ? (
                <LoadingIndicator
                  dotCount={3}
                  dotSize={4}
                  dotSpacing={3}
                  animationType="wave"
                  showText={false}
                />
              ) : (
                categorizedInstances.inProgress.length
              )}
            </div>
            <span className="text-xs uppercase tracking-widest font-mono  text-slate-600 dark:text-slate-400">
              In Progress
            </span>
          </div>
        </div>

        <div className="flex h-32 items-center justify-center rounded-2xl border border-green-200/80 bg-green-50 p-4 dark:border-transparent dark:bg-green-400/10">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="text-4xl font-bold text-green-500 min-h-12 flex items-center justify-center">
              {loading ? (
                <LoadingIndicator
                  dotCount={3}
                  dotSize={4}
                  dotSpacing={3}
                  animationType="wave"
                  showText={false}
                />
              ) : (
                categorizedInstances.last.length
              )}
            </div>
            <span className="font-mono text-xs uppercase tracking-widest text-green-700 dark:text-white/60">
              {categorizedInstances.lastLabel}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
